import { Session } from "@/interfaces/IRecap";
import i18n from "@/shared/i18n";
import "dayjs/locale/fa";
import moment from "moment-jalaali";
import { DateFormats } from "./DateFormatter";
moment.loadPersian({ usePersianDigits: false, dialect: "persian-modern" });

export const formatDayMonth = (date) => {
  const isJalali = i18n.language === "fa-IR";

  const dayOfWeek = moment(date)
    .locale(isJalali ? "fa" : "en")
    .format("dddd");

  const formattedDate = moment(date)
    .locale(isJalali ? "fa" : "en")
    .format(isJalali ? DateFormats.JDayMonthDate : DateFormats.DayMonthDate);

  return {
    dayOfWeek,
    formattedDate,
  };
};

export const formatMonthYear = (date) => {
  const isJalali = i18n.language === "fa-IR";

  const formattedDate = moment(date)
    .locale(isJalali ? "fa" : "en")
    .format(isJalali ? DateFormats.JMonthYearDate : DateFormats.MonthYearDate);

  return formattedDate;
};

export const formatDateHour = (
  startDate,
  endDate,
  format: "long" | "short" = "long"
) => {
  const isJalali = i18n.language === "fa-IR";

  const allDayDateFormat =
    format === "long"
      ? isJalali
        ? DateFormats.JLongDate
        : DateFormats.LongDate
      : isJalali
        ? DateFormats.JSimpleDate
        : DateFormats.SimpleDate;

  const hourlyDateFormat =
    format === "long"
      ? isJalali
        ? DateFormats.JLongDayWithTime
        : DateFormats.LongDayWithTime
      : DateFormats.HourAndMinute;

  if (!startDate || !endDate) {
    return {
      parsedStartDate: "",
      parsedEndDate: "",
    };
  }

  moment.locale(isJalali ? "fa" : "en");

  const start = moment(startDate);
  const end = moment(endDate);

  const diffInDays = end.diff(start, "minutes");

  if (diffInDays > 1440) {
    const parsedStartDate = start.format(allDayDateFormat);
    const parsedEndDate = end.subtract(1, "second").format(allDayDateFormat);

    return {
      parsedStartDate,
      parsedEndDate, // Show both start and end dates for multi-day events
    };
    // handle one day events covering 23:59 and 24:00 events
  } else if (diffInDays === 1440 || diffInDays === 1439) {
    const parsedStartDate = start.format(allDayDateFormat);

    return {
      parsedStartDate,
      parsedEndDate: null,
    };
  } else {
    const isSameDay = start.isSame(end, "day");
    const parsedStartDate = start.format(hourlyDateFormat);
    const parsedEndDate = end.format(
      isSameDay ? DateFormats.HourAndMinute : hourlyDateFormat
    );

    return { parsedStartDate, parsedEndDate };
  }
};

export const formatServerDate = (date?: string | null): string | null => {
  if (!date) return null;
  const m = moment(date);
  return m.isValid() ? m.format(DateFormats.WithTimeZone) : null;
};

export const formatDayMonthString = (date) => {
  const isJalali = i18n.language === "fa-IR";

  return moment(date)
    .locale(isJalali ? "fa" : "en")
    .format(
      isJalali ? DateFormats.JMonthDetailDate : DateFormats.MonthDetailDate
    );
};

export const formatDuration = (start: Date, end: Date): string => {
  const isJalali = i18n.language === "fa-IR";
  moment.locale(isJalali ? "fa" : "en");

  const startMoment = moment(start);
  const endMoment = moment(end);

  if (startMoment.isValid() && endMoment.isValid()) {
    const totalMinutes = endMoment.diff(startMoment, "minute");
    const totalSeconds = endMoment.diff(startMoment, "second");

    const hours = Math.floor(totalMinutes / 60);
    let minutes = totalMinutes % 60;

    if (minutes === 0 && totalSeconds > 0) {
      minutes = 1;
    }
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}`;
  } else {
    return "";
  }
};

export const formatTime = (dateString?: string) => {
  if (!dateString) return "";

  const isJalali = i18n.language === "fa-IR";
  moment.locale(isJalali ? "fa" : "en");

  const time = moment.utc(dateString).utcOffset(3.5 * 60);

  if (!time.isValid()) return "";

  return time.format("HH:mm");
};

export const formatDurationLabel = (
  start: Date | string,
  end: Date | string
): string => {
  // Detect Persian locale

  const isJalali = i18n.language === "fa-IR";

  // Convert to Moment objects (Moment can parse ISO strings with microseconds)
  const startMoment = moment(start);
  const endMoment = moment(end);

  // Return empty string if invalid
  if (!startMoment.isValid() || !endMoment.isValid()) return "";

  // Calculate total seconds
  const totalSeconds = endMoment.diff(startMoment, "seconds");
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  // Build duration parts
  const parts: string[] = [];

  if (hours > 0) parts.push(isJalali ? `${hours} ساعت` : `${hours} Hr`);
  if (minutes > 0) parts.push(isJalali ? `${minutes} دقیقه` : `${minutes} Min`);
  if (seconds > 0 || parts.length === 0)
    parts.push(isJalali ? `${seconds} ثانیه` : `${seconds} Sec`);

  return parts.join(" ");
};

export const formatPersianMeetingLabel = (meeting: Session): string => {
  const isJalali = i18n.language === "fa-IR";
  moment.locale(isJalali ? "fa" : "en");

  const startDate = moment(meeting.start);

  if (!startDate.isValid()) {
    return "";
  }

  const dayOfWeek = startDate.format("dddd");
  const day = startDate.format(isJalali ? "jD" : "D");
  const monthName = startDate.format(isJalali ? "jMMMM" : "MMMM");
  const startTime = startDate.format("HH:mm");

  // If end is null or invalid, only show start time
  if (!meeting.end) {
    if (isJalali) {
      return `${dayOfWeek} ${day} ${monthName}- ${startTime}`;
    } else {
      return `${dayOfWeek} ${monthName} ${day} - ${startTime}`;
    }
  }

  // If end exists, show both start and end times
  const endDate = moment(meeting.end);

  if (!endDate.isValid()) {
    if (isJalali) {
      return `${dayOfWeek} ${day} ${monthName}- ${startTime}`;
    } else {
      return `${dayOfWeek} ${monthName} ${day} - ${startTime}`;
    }
  }

  const endTime = endDate.format("HH:mm");

  // Format the label based on locale with time range
  if (isJalali) {
    return `${dayOfWeek} ${day} ${monthName}- ${startTime} تا ${endTime}`;
  } else {
    return `${dayOfWeek} ${monthName} ${day} - ${startTime} to ${endTime}`;
  }
};

export const formatTimeWithSeconds = (dateString?: string) => {
  if (!dateString) return "";

  const isJalali = i18n.language === "fa-IR";
  moment.locale(isJalali ? "fa" : "en");

  const time = moment.utc(dateString).utcOffset(3.5 * 60);

  if (!time.isValid()) return "";

  return time.format("HH:mm:ss");
};

export const getTimestampInSeconds = (dateString?: string): number => {
  if (!dateString) return 0;

  const time = moment.utc(dateString).utcOffset(3.5 * 60);

  if (!time.isValid()) return 0;

  return time.unix();
};

export const formatSecondsToTime = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )}:${String(seconds).padStart(2, "0")}`;
};

export const formatDurationText = (value?: number | null): string => {
  const totalSeconds = Math.floor(Number(value) || 0);

  if (totalSeconds <= 0) return "";

  if (totalSeconds < 60) {
    return i18n.t("recap.second", { second: totalSeconds });
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (hours > 0 && minutes > 0) {
    return i18n.t("recap.hourAndMin", {
      hour: hours,
      min: minutes,
    });
  }

  if (hours > 0) {
    return i18n.t("recap.hour", { hour: hours });
  }

  return i18n.t("recap.minute", { min: minutes });
};
